import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PledgeProductRead } from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import * as _ from 'lodash';
import {
  FULL_AMOUNT_TOLERANCE,
  roundTo,
  toNumber,
} from '../../versements-libres/services/versement-allocation';
import {
  PONCTUEL_KEYS,
  PROGRAMMED_KEYS,
  RachatKeys,
} from '../services/rachat-allocation';

export function contractTotalAmount(
  product: PledgeProductRead & { parameters?: any },
): number {
  return toNumber(product?.parameters?.currentSupportRepartition?.totalAmount);
}

export function initialRachatAmount(
  product: PledgeProductRead & { parameters?: any },
  keys: RachatKeys = PONCTUEL_KEYS,
): number {
  const saved = product?.parameters?.[keys.amount];
  if (_.isNil(saved)) return null;

  const contractTotal = contractTotalAmount(product);
  const hasDivestment = (product?.parameters?.[keys.supports] || []).some(
    support => toNumber(support?.amountDisinvest) > 0,
  );
  const isInitSentinel =
    !hasDivestment &&
    contractTotal > 0 &&
    Math.abs(toNumber(saved) - contractTotal) < FULL_AMOUNT_TOLERANCE;

  return isInitSentinel ? null : roundTo(saved, 2);
}

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  programmed = false;

  get keys(): RachatKeys {
    return this.programmed ? PROGRAMMED_KEYS : PONCTUEL_KEYS;
  }

  generateForm(
    entity: PledgeProductRead & { parameters: any },
    option?,
    keys: RachatKeys = this.keys,
  ) {
    const options: AbstractControlOptions = {};
    const contractTotal = contractTotalAmount(entity);
    const validators = [Validators.required, Validators.min(0.01)];
    if (contractTotal > 0) {
      validators.push(
        Validators.max(Math.max(0, roundTo(contractTotal - 0.01, 2))),
      );
    }

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        [keys.amount]: [initialRachatAmount(entity, keys), validators],
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  generate(entity: any, option) {
    this.programmed = !!option?.programmed;
    const keys = this.programmed ? PROGRAMMED_KEYS : PONCTUEL_KEYS;
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: (product, opts) => this.generateForm(product, opts, keys),
      propertyName: 'products',
    });
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }

  get rachatAmountControl() {
    return this.products.get(`${0}`).get('parameters').get(this.keys.amount);
  }

  get contractTotal(): number {
    return contractTotalAmount(this.entity?.products?.[0]);
  }

  extract() {
    const products = _.cloneDeep(this.form?.value?.products);
    products?.forEach(product => {
      const amount = product?.parameters?.[this.keys.amount];
      product.parameters = {
        [this.keys.amount]: _.isNil(amount) ? null : roundTo(amount, 2),
      };
    });
    return products;
  }
}
